import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { createShow, updateShow } from 'api/show'
import { handleFormSubmit } from 'components/formHandlers'
import { FileUploadUploadEvent } from 'primereact/fileupload'
import { useEffect, useRef, useState } from 'react'
import { useFieldArray, useForm, UseFormReset } from 'react-hook-form'
import { ImageType } from 'types/Image'
import { mapShowWithImagesToDTO, ShowImageData, ShowWithImages, ShowWithImagesDTO } from 'types/Show'

export interface UseShowFormProps {
    initialData?: ShowWithImages | any
    queryKey?: string[]
    onClose?: () => void
    partnerId?: string
}

interface OnSubmitProps {
    initialData?: ShowWithImages | null
    queryClient: QueryClient
    queryKey: string[]
    onClose?: () => void
    reset: UseFormReset<ShowWithImagesDTO>
    setLoading: (loading: boolean) => void
    partnerId?: string
}

export const handleShowFormSubmit = async (
    { initialData, queryClient, queryKey, onClose, reset, setLoading, partnerId }: OnSubmitProps,
    data: ShowWithImagesDTO,
) => {
    const isUpdate = !!initialData?.show_id

    const createEntity = isUpdate ? () => Promise.resolve() : createShow
    const updateEntity = isUpdate ? updateShow : () => Promise.resolve()

    await handleFormSubmit<ShowWithImages, ShowWithImagesDTO, 'show_id', string>(
        {
            initialData,
            queryClient,
            queryKey,
            onClose,
            reset,
            setLoading,
            createEntity,
            updateEntity,
            entityIdKey: 'show_id',
            partnerId,
        },
        data,
    )
}

export const useShowForm = ({ initialData, queryKey = ['shows'], onClose, partnerId }: UseShowFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        getValues,
    } = useForm<ShowWithImagesDTO>({
        defaultValues: initialData ? mapShowWithImagesToDTO(initialData) : {},
    })

    const queryClient = useQueryClient()
    const [loading, setLoading] = useState(false)
    const [uploadedImages, setUploadedImages] = useState<ShowImageData[]>(initialData?.images || [])
    const [coverImageOptions, setCoverImageOptions] = useState<ImageType[]>(initialData?.images || [])
    const [coverImage, setCoverImage] = useState<string | null>(initialData?.cover_image || null)

    const { fields, append, remove } = useFieldArray({ control, name: 'contributors' })
    const initialRender = useRef(true)

    useEffect(() => {
        if (initialRender.current) {
            if (fields.length === 0) {
                append({ role: '', responsible: '' })
            }
            initialRender.current = false
        }
    }, [fields, append])

    useEffect(() => {
        if (initialData) {
            const mappedData = mapShowWithImagesToDTO(initialData)
            Object.keys(mappedData).forEach((key) => {
                setValue(key as keyof ShowWithImagesDTO, mappedData[key as keyof ShowWithImagesDTO])
            })
        }
    }, [initialData, setValue])

    const onUpload = (event: FileUploadUploadEvent) => {
        try {
            const jsonResponse = JSON.parse(event.xhr.responseText)
            setUploadedImages((prevImages) => [...prevImages, ...jsonResponse.images])
        } catch (error) {
            console.error('Failed to parse JSON:', error)
        }
    }

    const onSubmit = async (data: ShowWithImagesDTO) => {
        const updatedData = { ...data, coverImage: coverImage ?? uploadedImages[0]?.image_id?.toString() }
        await handleShowFormSubmit(
            {
                initialData,
                queryClient,
                queryKey,
                onClose,
                reset,
                setLoading,
                partnerId,
            },
            { ...updatedData, images: uploadedImages },
        )
    }

    return {
        control,
        handleSubmit: handleSubmit(onSubmit),
        errors,
        loading,
        coverImageOptions,
        setUploadedImages,
        setCoverImageOptions,
        coverImage,
        setCoverImage,
        fields,
        append,
        remove,
        onUpload,
        setValue,
        getValues,
    }
}
